import { action, extendObservable, reaction } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import urlPatternValidator from '../../urlPatternValidator';

export class InputConfigurationStore {
    static PropType = PropTypes.shape({
        allInputStore: FlagStore.PropType.isRequired,
        clipboardPasteStore: FlagStore.PropType.isRequired,
        pasteLimitEnabledStore: FlagStore.PropType,
        pasteLimitStore: InputStore.PropType,
    });

    static pasteLimitValidator = (store) => (limit) => {
        const limitStr = limit + '';
        const limitInt = parseInt(limit, 10);

        if (limitStr.length > 5) {
            store.setValue(limitStr.substr(0, 5));
            return store.isValid;
        }

        return !isNaN(limitInt) && limitInt >= 0 && limitStr.length < 7 && limitStr === limitInt + '';
    }

    constructor(data = {}) {
        extendObservable(this, /** @class InputConfigurationStore */{
            allInput: false,
            clipboardPaste: false,
            pasteLimitEnabled: false,
            pasteLimit: 200,

            allInputStore: new FlagStore(false),
            clipboardPasteStore: new FlagStore(false),
            pasteLimitEnabledStore: new FlagStore(false),
            pasteLimitStore: new InputStore({ value: 200 }),

            get hasChanged() {
                return (this.allInputStore.value !== this.allInput)
                    || (this.clipboardPasteStore.value !== this.clipboardPaste)
                    || (this.pasteLimitEnabledStore.value !== this.pasteLimitEnabled)
                    || (this.pasteLimitStore.value !== String(this.pasteLimit));
            },
        }, data);

        this.pasteLimitStore.setValidator(InputConfigurationStore.pasteLimitValidator(this.pasteLimitStore));

        reaction(() => this.allInputStore.value, (newAllInputValue) => {
            if (newAllInputValue) {
                this.clipboardPasteStore.value = true;
            } else {
                this.clipboardPasteStore.value = false;
            }
        });

        reaction(() => this.clipboardPasteStore.value, (newPasteValue) => {
            if (newPasteValue) {
                this.pasteLimitEnabledStore.value = false;
            }
        });
    }

    setFromObject = action((data = {}, asChanges = false) => {
        if (!asChanges) {
            this.allInput = data.allInput;
            this.clipboardPaste = data.clipboardPaste;
        }

        this.allInputStore.setValue(data.allInput);
        this.clipboardPasteStore.setValue(data.clipboardPaste);

        if ('pasteLimitEnabled' in data) {
            if (!asChanges) {
                this.pasteLimitEnabled = data.pasteLimitEnabled;
            }
            this.pasteLimitEnabledStore.setValue(data.pasteLimitEnabled);
        }

        if ('pasteLimit' in data) {
            if (data.pasteLimit < 0) {
                data.pasteLimit = 0;
            }

            if (!asChanges) {
                this.pasteLimit = data.pasteLimit;
                this.pasteLimitStore.setTouched(false);
            }
            this.pasteLimitStore.setValue(data.pasteLimit);
        }
    });

    toObject = action(() => {
        return {
            allInput: this.allInputStore.value,
            clipboardPaste: this.clipboardPasteStore.value,
            pasteLimitEnabled: this.pasteLimitEnabledStore.value,
            pasteLimit: this.pasteLimitStore.value,
        };
    })
}

export class DomainConfigurationStore {
    static PropType = PropTypes.shape({
        domain: PropTypes.string.isRequired,
        options: InputConfigurationStore.PropType.isRequired,
        hasChanged: PropTypes.bool.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class DomainConfigurationStore */{
            domain: '',
            domainStore: new InputStore({ value: '' }),
            options: new InputConfigurationStore(),

            get hasChanged() {
                return (this.domainStore.value !== this.domain) || this.options.hasChanged;
            },
        });

        this.domainStore.setValidator(urlPatternValidator);
        this.setFromObject({ ...this, ...data });
    }

    setFromObject = action(({ domain, options }, markAsChanges = false) => {
        if (!markAsChanges) {
            this.domain = domain;
            this.domainStore.setTouched(false);
        }

        this.domainStore.setValue(domain);
        this.options.setFromObject(options, markAsChanges);
    });

    toObject = action(() => {
        return {
            domain: this.domainStore.value,
            options: this.options.toObject(),
        };
    });
}

export class DataLossPreventionStore {
    static PropType = PropTypes.shape({
        defaults: InputConfigurationStore.PropType.isRequired,
        domains: MobxPropTypes.arrayOrObservableArrayOf(DomainConfigurationStore.PropType).isRequired,
        search: InputStore.PropType.isRequired,
        hasChanged: PropTypes.bool.isRequired,

        domainConfigurationModal: FlagStore.PropType.isRequired,
        domainConfigurationModalStore: PropTypes.shape({
            domainConfig: DomainConfigurationStore.PropType.isRequired,
            urlGuidelines: FlagStore.PropType,
            index: PropTypes.number,
        }).isRequired,

        addDomain: PropTypes.func.isRequired,
        editDomain: PropTypes.func.isRequired,
        removeDomain: PropTypes.func.isRequired,
        canSubmit: PropTypes.func,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class DataLossPreventionStore */{
            defaults: new InputConfigurationStore(),
            domains: [],
            domainsOriginalLength: 0,
            customerId: 0, // TODO: WHY
            menuId: null,
            search: new InputStore({ value: '' }),
            domainConfigurationModal: new FlagStore(false),
            domainConfigurationModalStore: {
                domainConfig: new DomainConfigurationStore(),
                urlGuidelines: new FlagStore(),
                index: null,
            },

            get hasChanged() {
                return this.defaults.hasChanged
                    || this.domains.some((store) => store.hasChanged)
                    || this.domainsOriginalLength !== this.domains.length;
            },

            get isValid() {
                return this.defaults.pasteLimitStore.isValid;
            },

            get filteredDomains() {
                const q = this.search.value.trim().toLowerCase();

                if (!q) {
                    return this.domains;
                }

                return this.domains.filter((domain) => domain.domainStore.value.toLowerCase().indexOf(q) !== -1);
            },

            canSubmit() {
                const { options, domainStore } = this.domainConfigurationModalStore.domainConfig;

                return options.pasteLimitStore.isValid &&
                    !domainStore.isEmpty && (domainStore.isTouched || domainStore.isValid);
            },
        }, data);
    }

    showCreateDomainConfigurationModal = action(() => {
        const options = this.defaults;
        this.domainConfigurationModalStore.domainConfig = new DomainConfigurationStore({
            domain: '',
            options: options.toObject(),
        });
        this.domainConfigurationModalStore.index = null;
        this.domainConfigurationModal.open();
    });

    showEditDomainConfigurationModal = action((options, index) => {
        this.domainConfigurationModalStore.domainConfig = new DomainConfigurationStore(options.toObject());
        this.domainConfigurationModalStore.index = index;

        this.domainConfigurationModal.open();
    });

    setFromObject = action(({ defaults, domains = [], customerId }) => {
        this.defaults.setFromObject(defaults);
        this.domains = domains.map((data) => {
            const domainConfig = new DomainConfigurationStore();
            domainConfig.setFromObject(data);
            return domainConfig;
        });
        this.domainsOriginalLength = this.domains.length;
        this.customerId = customerId;
        this.search.setValue('');
    });

    toObject = () => ({
        defaults: this.defaults.toObject(),
        domains: this.domains.map((domain) => domain.toObject()),
        customerId: this.customerId,
    });

    doSubmit = action(() => {
        const { index, domainConfig } = this.domainConfigurationModalStore;
        const { domainStore } = domainConfig;

        domainStore.setTouched(false);

        if (!domainStore.isValid) {
            domainStore.focus();
            return;
        }

        if (index === null) {
            this.addDomain(domainConfig);
        } else {
            this.editDomain(domainConfig, index);
        }

        this.domainConfigurationModal.close();
    });

    addDomain = action((domainConfigurationStore) => {
        this.domains.push(domainConfigurationStore);
    });

    editDomain = action((domainConfigurationStore, index) => {
        this.domains[index].setFromObject(domainConfigurationStore.toObject(), true);
    });

    removeDomain = action((index) => {
        this.domains.splice(index, 1);
    });

    toggleMenu = action((id) => {
        if (id === this.menuId) {
            this.menuId = null;
        } else {
            this.menuId = id;
        }
    });
}
